import { i18n } from "@lingui/core"

import { getValidateEmailUrl, VALID_EMAIL } from "../constants"
import { debouncePromise } from "../debounce-promise"
import { getHubspotBlockedEmailLocalizedMessage } from "../requests/hubspot"
import { EMAIL_REGEX } from "../validation"

export const requiredField = (msg: () => string) => (value: string) =>
  !value ? msg() : ""

export const validateOtherField = requiredField(() =>
  i18n._("Please enter the other value"),
)
export const validatePayoutToField = requiredField(() =>
  i18n._("Select who you are sending to"),
)
export const validateLeadField = requiredField(() =>
  i18n._("Select how you heard about us"),
)
export const validateFirstName = requiredField(() =>
  i18n._("A first name is required"),
)
export const validateLastName = requiredField(() =>
  i18n._("A last name is required"),
)
export const validateJobTitle = requiredField(() =>
  i18n._("A job title is required"),
)
export const validateSpend = requiredField(() =>
  i18n._("Select your annual spend amount"),
)

const getValidateEmailText = (email: string) =>
  fetch(getValidateEmailUrl(email))
    .then(res => {
      if (res.status !== 200) {
        throw new Error(res.statusText)
      }
      return res.text()
    })
    .catch(error => {
      // eslint-disable-next-line no-console
      console.error("validateEmailWithApi error", error)
      // In case of error we want to return true to avoid blocking the user
      return VALID_EMAIL
    })

const emailValidationResultsCache = new Map<string, Promise<string>>()
export const validateEmailWithApi = async (email: string) => {
  const cachedResult = emailValidationResultsCache.get(email)
  if (cachedResult) {
    return cachedResult
  }
  const response = getValidateEmailText(email)
  emailValidationResultsCache.set(email, response)
  return response
}

// Ability to switch on/off neverbounce via vercel
const debouncedValidateEmailWithApi =
  process.env.GATSBY_ENABLE_NEVERBOUNCE === "true"
    ? debouncePromise(validateEmailWithApi, 800)
    : () => VALID_EMAIL

export const isDomainInBlacklist = (blacklist: string[], email: string) => {
  const userEmailDomain = email.split("@")?.[1]?.toLowerCase()
  const isBlocked = blacklist.some(domain => domain === userEmailDomain)
  return isBlocked
}

export const validateEmail =
  (invalidEmails: string[] = [], blacklist: string[] = []) =>
  async (value: string) => {
    if (invalidEmails.includes(value) || !value || !EMAIL_REGEX.test(value)) {
      return getHubspotBlockedEmailLocalizedMessage()
    }

    // we do not want this inside the debouncedValidateEmailWithApi to avoid debounce wait
    if (emailValidationResultsCache.has(value)) {
      const cacheResult = await emailValidationResultsCache.get(value)
      return cacheResult === VALID_EMAIL
        ? ""
        : getHubspotBlockedEmailLocalizedMessage()
    }
    // moved separately so we do not have to iterate over blacklist every time we validate.
    // it has ~6000 items
    if (isDomainInBlacklist(blacklist, value)) {
      return getHubspotBlockedEmailLocalizedMessage()
    }
    return (await debouncedValidateEmailWithApi(value)) === VALID_EMAIL
      ? ""
      : getHubspotBlockedEmailLocalizedMessage()
  }
